import { useMemo } from "react";
import { useTranslations } from "use-intl";

import type { FilterOption } from "@/components/ui/filter-select";
import { formatFormatSetValue } from "@/lib/formatFormatSetValue";
import { formatTranslatedCatalogTaxonomyLabel } from "@/lib/music-taxonomy";
import { formatCountry, getFlagUrl } from "@garimpa/lib/countries";

interface FacetCount {
  value: string;
  count: number;
}

interface ReleaseFacets {
  formatSets?: FacetCount[];
  countries?: FacetCount[];
  years?: FacetCount[];
  labels?: FacetCount[];
  companies?: FacetCount[];
  tags?: FacetCount[];
}

interface ReleaseFilterOptions {
  formatSets: FilterOption[];
  countries: FilterOption[];
  years: FilterOption[];
  labels: FilterOption[];
  companies: FilterOption[];
  tags: FilterOption[];
}

function formatReleaseTagTerm(
  value: string,
  tMediaFeature: Parameters<typeof formatTranslatedCatalogTaxonomyLabel>[1],
) {
  if (value.startsWith("feature:")) {
    return formatTranslatedCatalogTaxonomyLabel(value.slice("feature:".length), tMediaFeature);
  }
  if (value.startsWith("detail:")) {
    return value.slice("detail:".length);
  }
  return formatTranslatedCatalogTaxonomyLabel(value, tMediaFeature);
}

function buildReleaseFilterOptions(
  facets: ReleaseFacets | undefined,
  locale: string,
  tMediaFeature: Parameters<typeof formatReleaseTagTerm>[1],
  formatLabel: Parameters<typeof formatFormatSetValue>[1],
): ReleaseFilterOptions {
  return {
    formatSets: buildFormatSetOptions(facets?.formatSets, formatLabel),
    countries: buildCountryOptions(facets?.countries, locale),
    years: (facets?.years ?? []).map(facetAsOption).sort((a, b) => b.value.localeCompare(a.value)),
    labels: (facets?.labels ?? []).map(facetAsOption).sort(descendingCount),
    companies: (facets?.companies ?? []).map(facetAsOption).sort(descendingCount),
    tags: buildTagOptions(facets?.tags, tMediaFeature),
  };
}

function buildFormatSetOptions(
  facets: FacetCount[] | undefined,
  formatLabel: Parameters<typeof formatFormatSetValue>[1],
): FilterOption[] {
  return (facets ?? []).map((facet) => ({
    ...facetAsOption(facet),
    label: formatFormatSetValue(facet.value, formatLabel),
  }));
}

function buildCountryOptions(facets: FacetCount[] | undefined, locale: string): FilterOption[] {
  return (facets ?? [])
    .map((facet) => ({
      value: facet.value,
      label: formatCountry(facet.value, locale, { includeUnknown: true }),
      count: facet.count,
      iconUrl: getFlagUrl(facet.value, 20) ?? undefined,
    }))
    .sort(descendingCount);
}

function buildTagOptions(
  facets: FacetCount[] | undefined,
  tMediaFeature: Parameters<typeof formatReleaseTagTerm>[1],
): FilterOption[] {
  return (facets ?? [])
    .map((facet) => ({
      ...facetAsOption(facet),
      label: formatReleaseTagTerm(facet.value, tMediaFeature),
    }))
    .sort(descendingCount);
}

function facetAsOption(facet: FacetCount): FilterOption {
  return { value: facet.value, label: facet.value, count: facet.count };
}

function descendingCount(a: FilterOption, b: FilterOption): number {
  return (b.count ?? 0) - (a.count ?? 0);
}

export function useReleaseFilterOptions(facets: ReleaseFacets | undefined, locale: string) {
  const tMediaFeature = useTranslations("shared.mediaFeatures");
  const tReleaseFormat = useTranslations("shared.releaseFilters");

  return useMemo(
    () =>
      buildReleaseFilterOptions(facets, locale, tMediaFeature, (format) => tReleaseFormat(format)),
    [facets, locale, tMediaFeature, tReleaseFormat],
  );
}
